/**
 * Zod validation schemas for the Kerntakencatalogus (Epic 2 — Kerntaken v2)
 *
 * Story 2.1: catalogus-schemas (naam/domein/vaardigheden/competenties).
 * Story 3.1 breidt dit bestand uit met de toewijzings-spelregels (percentages,
 * tijdvak-grenzen) — zie ARCHITECTURE-SPINE.md AD-10 ("de spelregel-schemas...
 * staan in packages/shared/src/schemas/coreTasks.ts en worden door frontend
 * én backend gebruikt").
 */
import { z } from 'zod';
/**
 * Eén vaardigheid (anker) — vrije tekst, niet-leeg na trim.
 */
export const coreTaskSkillSchema = z.string().trim().min(1).max(255);
/**
 * Schema voor het aanmaken van een kerntaak.
 *
 * `skills` is een array in exact de volgorde die de UI toont — deze volgorde
 * bepaalt `sortOrder` bij het inserten (Story 2.1 Task 1/2).
 * `competencyIds` moet 1–3 bestaande vak-competenties bevatten.
 */
export const createCoreTaskSchema = z.object({
    name: z.string().trim().min(1).max(255),
    domain: z.string().trim().min(1).max(255),
    skills: z.array(coreTaskSkillSchema).min(1),
    competencyIds: z.array(z.number().int().positive()).min(1).max(3),
});
/**
 * Schema voor het bewerken van een kerntaak.
 *
 * Elk veld is optioneel (partial update van de kerntaak-basisrij), maar
 * `skills`/`competencyIds` representeren — wanneer meegestuurd — ALTIJD de
 * volledige, vervangende lijst (geen partial-array-merge). De frontend
 * (Story 2.2) moet dus altijd de complete lijst meesturen wanneer ze wijzigt.
 */
export const updateCoreTaskSchema = z
    .object({
    name: z.string().trim().min(1).max(255).optional(),
    domain: z.string().trim().min(1).max(255).optional(),
    skills: z.array(coreTaskSkillSchema).min(1).optional(),
    competencyIds: z.array(z.number().int().positive()).min(1).max(3).optional(),
})
    .refine((data) => data.name !== undefined || data.domain !== undefined || data.skills !== undefined || data.competencyIds !== undefined, { message: 'At least one field must be provided for update' });
/**
 * Story 3.1 — Toewijzing met tijdspercentages en tijdvakken (AD-2, AD-10)
 *
 * Deze schemas valideren ALLEEN shape en eenvoudige per-veld-regels
 * (percentage-veelvoud, array-lengte, datumformaat). De STATEFUL regels
 * (overlap met bestaande tijdvakken, hoogstens 1 open tijdvak,
 * immutabiliteit door snapshot) horen thuis in `assignmentService`
 * (backend) omdat die de database moeten raadplegen — zod kan dat niet.
 */
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
/** Eén toewijzingsregel binnen een tijdvak. */
export const assignmentLineSchema = z.object({
    coreTaskId: z.number().int().positive(),
    timePercentage: z.number().int().min(10).max(100).multipleOf(10),
});
/**
 * Eén tijdvak-request (gebruikt door zowel POST als PUT
 * /api/v1/employees/:id/task-periods). `endDate` is `null` voor een open
 * tijdvak. `exceptionMotivation` is verplicht zodra er méér dan 3
 * toewijzingen zijn (4e/5e kerntaak, AC2/AC3 van Story 3.1).
 */
export const periodInputSchema = z
    .object({
    startDate: z.string().regex(ISO_DATE_REGEX, 'startDate moet het formaat YYYY-MM-DD hebben'),
    endDate: z
        .string()
        .regex(ISO_DATE_REGEX, 'endDate moet het formaat YYYY-MM-DD hebben')
        .nullable()
        .optional(),
    exceptionMotivation: z.string().trim().min(1).nullable().optional(),
    assignments: z.array(assignmentLineSchema).min(1).max(5),
})
    .refine((data) => data.assignments.reduce((sum, a) => sum + a.timePercentage, 0) === 100, { message: 'De som van alle percentages moet exact 100 zijn', path: ['assignments'] })
    .refine((data) => data.assignments.length <= 3 || Boolean(data.exceptionMotivation && data.exceptionMotivation.trim().length > 0), { message: 'Een motivatie is verplicht bij een 4e of 5e kerntaak', path: ['exceptionMotivation'] });
/**
 * Story 4.1 (voorbereidend) / Story 4.4 — pakket-snapshot bij finalize (AD-5)
 *
 * Gedenormaliseerde bevriezing van het pakket (tijdvakken + kerntaken) op
 * het moment van `finalize`, zodat een afgeronde beoordeling nooit meer
 * wijzigt door latere catalogus- of toewijzingswijzigingen. **Exact deze
 * camelCase-sleutels zijn bindend** (Gepinde identifiers,
 * ARCHITECTURE-SPINE.md) — Story 4.4 schrijft en leest uitsluitend via dit
 * schema.
 */
export const packageSnapshotCoreTaskSchema = z.object({
    coreTaskId: z.number().int().positive(),
    name: z.string(),
    domain: z.string(),
    timePercentage: z.number().int().min(10).max(100),
});
export const packageSnapshotPeriodSchema = z.object({
    startDate: z.string().regex(ISO_DATE_REGEX, 'startDate moet het formaat YYYY-MM-DD hebben'),
    endDate: z
        .string()
        .regex(ISO_DATE_REGEX, 'endDate moet het formaat YYYY-MM-DD hebben')
        .nullable(),
    weight: z.number().min(0).max(1),
    coreTasks: z.array(packageSnapshotCoreTaskSchema).min(1),
    exceptionMotivation: z.string().nullable().optional(),
});
export const packageSnapshotSchema = z.object({
    periods: z.array(packageSnapshotPeriodSchema).min(1),
});
/**
 * Story 6.1 — Migratieservice met dry-run en gecontroleerde activering (AD-8)
 *
 * `migrationMoment` is een expliciete, door de admin ingevoerde datum (1e
 * van een maand) -- niet hardcoded, want het migratiemoment is een
 * operationeel besluit (zie Story 6.1 Dev Notes Task 3).
 */
/**
 * Alleen shape-check (YYYY-MM-DD) + "1e van een maand"; de maand-range
 * (01-12) wordt HIER expliciet meegevalideerd (niet alleen digit-shape) --
 * anders zouden onmogelijke datums als '2026-13-01' de shape-regex en de
 * '-01$'-check allebei doorstaan en pas veel later (of nooit) als foutieve
 * data zichtbaar worden (adversarial review-fix, zie Dev Notes Story 6.1).
 */
const MIGRATION_MOMENT_REGEX = /^\d{4}-(0[1-9]|1[0-2])-01$/;
export const migrationRequestSchema = z.object({
    dryRun: z.boolean(),
    migrationMoment: z
        .string()
        .regex(ISO_DATE_REGEX, 'migrationMoment moet het formaat YYYY-MM-DD hebben')
        .regex(MIGRATION_MOMENT_REGEX, 'migrationMoment moet de 1e van een geldige kalendermaand zijn (YYYY-MM-01, maand 01-12)'),
});
/**
 * Story 6.3 — Migratie-tab met activerings-gate
 *
 * Gebruikt voor zowel "Impactrapport gereviewd door [naam]" als
 * "Handleiding herschreven en gepubliceerd door [naam]" -- beide leggen
 * naam + timestamp (server-side) vast in `system_settings` + audit.
 */
export const reviewNameSchema = z.object({
    name: z.string().trim().min(1).max(255),
});
//# sourceMappingURL=coreTasks.js.map